import { useEffect, useMemo, useRef } from "react"

import type { AxiosError } from "axios"

import {
  deleteStyleTemplateDraft,
  getStyleTemplateDraft,
  upsertStyleTemplateDraft,
} from "@/features/studio-ai/api/style-templates-api"
import { SettingsService } from "@/services/settings-service"

import { useStyleTemplateEditorStore } from "../stores/style-template-editor-store"

const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000
const DRAFT_STORAGE_VERSION = 1
const SERVER_SYNC_BACKOFF_MS = 60_000

type StyleTemplateDraftPayload = {
  version: number
  updatedAt: number
  title: string
  description: string
  promptFragment: string
  brandColors: string[]
  visibility: "personal" | "company"
}

function getDraftKey(): string {
  const settingsService = SettingsService.getInstance()
  const userId = settingsService.claims.oid || settingsService.claims.sub || "anonymous"
  return `studio-ai:style-template-draft:v${DRAFT_STORAGE_VERSION}:${userId}`
}

export function clearStyleTemplateDraft(): void {
  if (typeof window === "undefined") return
  try {
    window.localStorage.removeItem(getDraftKey())
  } catch {
    // Ignore storage cleanup errors
  }
  void deleteStyleTemplateDraft().catch(() => {
    // Cloud cleanup is best effort; local cleanup already done
  })
}

function shouldBackoffServerSync(error: unknown): boolean {
  const axiosError = error as AxiosError<{ error?: string }>
  const status = axiosError.response?.status
  return typeof status === "number" && status >= 500
}

export function useStyleTemplateDraftAutosave({
  skipRestore = false,
}: { skipRestore?: boolean } = {}) {
  const loadRevision = useStyleTemplateEditorStore((state) => state.loadRevision)
  const title = useStyleTemplateEditorStore((state) => state.title)
  const description = useStyleTemplateEditorStore((state) => state.description)
  const promptFragment = useStyleTemplateEditorStore((state) => state.promptFragment)
  const brandColors = useStyleTemplateEditorStore((state) => state.brandColors)
  const visibility = useStyleTemplateEditorStore((state) => state.visibility)
  const isDirty = useStyleTemplateEditorStore((state) => state.isDirty)
  const setAutosaveStatus = useStyleTemplateEditorStore((state) => state.setAutosaveStatus)
  const setSyncStatus = useStyleTemplateEditorStore((state) => state.setSyncStatus)
  const setLastDraftSavedAt = useStyleTemplateEditorStore((state) => state.setLastDraftSavedAt)
  const setRestoreCandidate = useStyleTemplateEditorStore((state) => state.setRestoreCandidate)
  const restoredRevisionRef = useRef<number | null>(null)
  const serverSyncTimeoutRef = useRef<number | null>(null)
  const nextServerSyncAllowedAtRef = useRef<number>(0)

  const storageKey = useMemo(() => getDraftKey(), [])

  useEffect(() => {
    if (skipRestore) return
    if (restoredRevisionRef.current === loadRevision) return
    restoredRevisionRef.current = loadRevision

    if (typeof window === "undefined") return

    const run = async () => {
      try {
        let localDraft: StyleTemplateDraftPayload | null = null
        try {
          const raw = window.localStorage.getItem(storageKey)
          if (raw) {
            const payload = JSON.parse(raw) as StyleTemplateDraftPayload
            if (
              payload.updatedAt &&
              payload.version === DRAFT_STORAGE_VERSION &&
              Date.now() - payload.updatedAt <= DRAFT_TTL_MS
            ) {
              localDraft = payload
            } else {
              window.localStorage.removeItem(storageKey)
            }
          }
        } catch {
          setAutosaveStatus("error")
        }

        let serverDraft = null
        try {
          serverDraft = await getStyleTemplateDraft()
        } catch (error) {
          if (shouldBackoffServerSync(error)) {
            nextServerSyncAllowedAtRef.current = Date.now() + SERVER_SYNC_BACKOFF_MS
            setSyncStatus("sync_error")
          }
        }
        const serverUpdatedAt = serverDraft?.updated_at
          ? new Date(serverDraft.updated_at).getTime()
          : 0
        const localUpdatedAt = localDraft?.updatedAt ?? 0

        const hasServerDraft = Boolean(serverDraft && serverUpdatedAt > 0)
        const hasLocalDraft = Boolean(localDraft && localUpdatedAt > 0)

        if (!hasServerDraft && !hasLocalDraft) {
          setRestoreCandidate(null)
          return
        }

        if (hasServerDraft && (!hasLocalDraft || serverUpdatedAt >= localUpdatedAt)) {
          setRestoreCandidate({
            source: "server",
            updatedAt: serverUpdatedAt,
            title: serverDraft?.title || "",
            description: serverDraft?.description || "",
            promptFragment: serverDraft?.prompt_fragment || "",
            brandColors: serverDraft?.brand_colors || [],
            visibility: serverDraft?.visibility === "company" ? "company" : "personal",
          })
          return
        }

        if (localDraft) {
          setRestoreCandidate({
            source: "local",
            updatedAt: localDraft.updatedAt,
            title: localDraft.title,
            description: localDraft.description,
            promptFragment: localDraft.promptFragment,
            brandColors: localDraft.brandColors,
            visibility: localDraft.visibility,
          })
        }
      } catch {
        setAutosaveStatus("error")
      }
    }

    void run()
  }, [
    skipRestore,
    loadRevision,
    setAutosaveStatus,
    setRestoreCandidate,
    setLastDraftSavedAt,
    storageKey,
  ])

  useEffect(() => {
    if (!isDirty) return

    setAutosaveStatus("saving")
    setSyncStatus("saving_local")
    const timeout = window.setTimeout(() => {
      try {
        const payload: StyleTemplateDraftPayload = {
          version: DRAFT_STORAGE_VERSION,
          updatedAt: Date.now(),
          title,
          description,
          promptFragment,
          brandColors,
          visibility,
        }

        window.localStorage.setItem(storageKey, JSON.stringify(payload))
        setLastDraftSavedAt(payload.updatedAt)
        setAutosaveStatus("saved")
        setSyncStatus("saved_local")
      } catch {
        setAutosaveStatus("error")
        setSyncStatus("sync_error")
      }
    }, 1000)

    return () => window.clearTimeout(timeout)
  }, [
    isDirty,
    title,
    description,
    promptFragment,
    brandColors,
    visibility,
    storageKey,
    setAutosaveStatus,
    setSyncStatus,
    setLastDraftSavedAt,
  ])

  useEffect(() => {
    if (!isDirty) return
    if (Date.now() < nextServerSyncAllowedAtRef.current) {
      setSyncStatus("sync_error")
      return
    }

    if (serverSyncTimeoutRef.current) {
      window.clearTimeout(serverSyncTimeoutRef.current)
    }

    serverSyncTimeoutRef.current = window.setTimeout(async () => {
      try {
        setSyncStatus("syncing")
        const saved = await upsertStyleTemplateDraft({
          title,
          description,
          prompt_fragment: promptFragment,
          visibility,
          brand_colors: brandColors,
        })
        setSyncStatus("synced")
        if (saved.updated_at) {
          setLastDraftSavedAt(new Date(saved.updated_at).getTime())
        }
      } catch {
        nextServerSyncAllowedAtRef.current = Date.now() + SERVER_SYNC_BACKOFF_MS
        setSyncStatus("sync_error")
      }
    }, 3000)

    return () => {
      if (serverSyncTimeoutRef.current) {
        window.clearTimeout(serverSyncTimeoutRef.current)
      }
    }
  }, [
    isDirty,
    title,
    description,
    promptFragment,
    visibility,
    brandColors,
    setLastDraftSavedAt,
    setSyncStatus,
  ])
}
